import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../context/AuthContext';
import { useBeatDetails } from '../hooks/useBeatDetails';
import VerseRecorder from '../components/verses/VerseRecorder';
import RecordingAudioCheckModal from '../components/verses/RecordingAudioCheckModal';
import VersesList from '../components/verses/VersesList';
import ConfirmDeleteModal from '../components/beats/ConfirmDeleteModal';
import {
  isRecordingAudioCheckPassed,
  setRecordingAudioCheckPassed,
} from '../utils/recordingAudioCheckSession';
import './BeatDetails.css';

type TabId = 'details' | 'verses';

const WAVE_BARS = 48;

const VERSES_LIMIT_TOOLTIP =
  'This limit is set by the producer of this beat. It determines how many verses can be recorded on it.';

const VersesLimitHeading: React.FC = () => {
  const tipId = 'beat-details-verses-limit-tip';

  return (
    <div className="beat-preview__verses-limit-title-wrap">
      <p className="beat-preview__verses-limit-title">Verses Limit</p>
      <button
        type="button"
        className="beat-preview__info-tip"
        aria-label="What is the verses limit?"
        aria-describedby={tipId}
      >
        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
            clipRule="evenodd"
          />
        </svg>
        <span id={tipId} className="beat-preview__info-tip-bubble" role="tooltip">
          {VERSES_LIMIT_TOOLTIP}
        </span>
      </button>
    </div>
  );
};

const BeatDetailsPage: React.FC = () => {
  const { authState } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('details');
  const [showAudioCheck, setShowAudioCheck] = useState(false);
  const {
    beatId,
    beat,
    isLoading,
    error,
    showRecorder,
    setShowRecorder,
    versesKey,
    audioRef,
    verseCount,
    hasPublishedMix,
    isVoting,
    userVote,
    voteCount,
    showDeleteModal,
    setShowDeleteModal,
    isDeleting,
    deleteError,
    isUpdatingVersesCount,
    isBeatPlaying,
    togglePlay,
    handleVote,
    handleVersesCountChange,
    handleDeleteBeat,
    handleVerseUploaded,
    stop,
    progressPercent,
    displayCurrent,
    displayDuration,
  } = useBeatDetails();

  const [justSubmitted, setJustSubmitted] = useState(false);

  const requestRecording = () => {
    setJustSubmitted(false);
    stop();
    if (isRecordingAudioCheckPassed()) {
      setShowRecorder(true);
    } else {
      setShowAudioCheck(true);
    }
  };

  const handleAudioCheckPassed = () => {
    setRecordingAudioCheckPassed();
    setShowAudioCheck(false);
    setShowRecorder(true);
  };

  const isProducer = authState.user && beat && beat.producer_id === authState.user.id;
  const isListener = authState.user?.role === 'listener';
  const isCollabBeat = beat?.beat_type === 'collaboration';
  const canRap =
    !!authState.user &&
    (authState.user.role === 'rapper' || authState.user.role === 'producer_rapper');

  // Cypher: cap is verses_count. Collab: open until the producer publishes one mix.
  const canRecord =
    canRap &&
    !!beat &&
    (isCollabBeat ? !hasPublishedMix : verseCount < beat.verses_count);

  const verseLimitReached =
    canRap && !!beat && !isCollabBeat && verseCount >= beat.verses_count;

  // On a collaboration beat the take is a submission, so confirm where it went.
  const onVerseUploaded = () => {
    handleVerseUploaded();
    if (isCollabBeat) setJustSubmitted(true);
  };

  if (isLoading) {
    return (
      <div className="beat-preview beat-preview--loading">
        <div className="beat-preview__spinner" aria-label="Loading" />
        <p>Loading beat...</p>
      </div>
    );
  }

  if (error || !beat) {
    return (
      <div className="beat-preview beat-preview--error">
        <p>{error || 'Beat not found'}</p>
        <Link to="/home" className="beat-preview__link-back">
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="beat-preview">
      <Helmet>
        <title>DHH Collab | {beat.title}</title>
        <meta name="description" content={`Listen to ${beat.title} on DHH Collab.`} />
      </Helmet>

      <section className="beat-preview__hero">
        <div className="beat-preview__hero-inner max-w-6xl mx-auto container-padding">
          <div className="beat-preview__player-card">
            <div className="beat-preview__player-top">
              <button
                type="button"
                onClick={togglePlay}
                className={`beat-preview__play-btn ${isBeatPlaying ? 'beat-preview__play-btn--active' : ''}`}
                aria-label={isBeatPlaying ? 'Pause' : 'Play'}
              >
                {isBeatPlaying ? (
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M6 5h4v14H6V5zm8 0h4v14h-4V5z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M8 5v14l11-7L8 5z" />
                  </svg>
                )}
              </button>

              <div className="beat-preview__track-meta">
                <h1 className="beat-preview__title">{beat.title}</h1>
                <p className="beat-preview__artist">
                  <Link to={`/profile/${beat.producer_id}`} className="beat-preview__artist-link">
                    {beat.producer?.username}
                  </Link>
                  {beat.producer?.state && (
                    <span className="beat-preview__location"> · {beat.producer.state}</span>
                  )}
                </p>
              </div>

              <div className="beat-preview__hero-actions">
                {isListener && (
                  <button
                    type="button"
                    onClick={() => handleVote(userVote === 1 ? -1 : 1)}
                    disabled={isVoting}
                    className={`beat-preview__action-btn beat-preview__action-btn--vote ${userVote === 1 ? 'is-active' : ''}`}
                    aria-label={userVote === 1 ? 'Remove vote' : 'Vote'}
                  >
                    <svg viewBox="0 0 24 24" fill={userVote === 1 ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                      <path d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 6.364L12 20.364l-7.682-7.682a4.5 4.5 0 010-6.364z" />
                    </svg>
                    <span>{voteCount || 0}</span>
                  </button>
                )}

                {canRecord && (
                  <button
                    type="button"
                    onClick={requestRecording}
                    className="beat-preview__action-btn beat-preview__action-btn--record"
                    title="Record verse"
                  >
                    <span className="beat-preview__record-dot" />
                    Record
                  </button>
                )}

                {isProducer && (
                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(true)}
                    disabled={isDeleting}
                    className="beat-preview__action-btn beat-preview__action-btn--danger"
                    title="Delete beat"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>

            <div className={`beat-preview__waveform ${isBeatPlaying ? 'beat-preview__waveform--playing' : ''}`}>
              {Array.from({ length: WAVE_BARS }).map((_, i) => {
                const h = 25 + Math.abs(Math.sin(i * 0.55) * 55) + (i % 5) * 4;
                return (
                  <span
                    key={i}
                    className="beat-preview__wave-bar"
                    style={
                      {
                        '--bar-i': i,
                        '--bar-height': `${h}%`,
                      } as React.CSSProperties
                    }
                  />
                );
              })}
            </div>

            <div className="beat-preview__progress-row">
              <span className="beat-preview__time">{displayCurrent}</span>
              <div className="beat-preview__progress-track">
                <div
                  className="beat-preview__progress-fill"
                  style={{ width: `${isBeatPlaying ? progressPercent : 0}%` }}
                />
              </div>
              <span className="beat-preview__time">{displayDuration}</span>
            </div>

            <div className="beat-preview__meta-row">
              <p>
                <span className="beat-preview__meta-label">Genre:</span> {beat.genre}
              </p>
              <p>
                <span className="beat-preview__meta-label">BPM:</span> {beat.bpm}
              </p>
              <p className="beat-preview__meta-row--full">
                <span className="beat-preview__meta-label">Producer Location:</span>{' '}
                {beat.producer?.state || '—'}
              </p>
            </div>

            {isCollabBeat && (justSubmitted || canRecord || isProducer || hasPublishedMix) && (
              <div
                className={`beat-preview__collab-note ${
                  justSubmitted ? 'beat-preview__collab-note--sent' : ''
                }`}
              >
                <span className="beat-preview__collab-note-icon" aria-hidden>
                  {justSubmitted ? '✓' : hasPublishedMix ? '🎧' : '🤝'}
                </span>
                <div>
                  <p className="beat-preview__collab-note-title">
                    {justSubmitted
                      ? 'Sent to the producer'
                      : hasPublishedMix
                        ? 'Mix verse published'
                        : isProducer
                          ? 'Collaboration beat'
                          : 'This is a collab beat'}
                  </p>
                  <p className="beat-preview__collab-note-text">
                    {justSubmitted ? (
                      <>
                        {beat.producer?.username || 'The producer'} will listen and decide
                        whether to publish it. Until then only the two of you can hear it, and
                        you can delete it from your profile any time.
                      </>
                    ) : hasPublishedMix ? (
                      <>
                        This beat already has its mix verse. Recording is closed — listen to
                        the published take below.
                      </>
                    ) : isProducer ? (
                      <>
                        Takes recorded here stay private until you publish one as the mix.{' '}
                        <Link to="/submissions" className="beat-preview__collab-note-link">
                          Review submissions
                        </Link>
                      </>
                    ) : (
                      <>
                        Your take goes to {beat.producer?.username || 'the producer'} first —
                        they pick one mix verse, then recording closes.
                      </>
                    )}
                  </p>
                </div>
              </div>
            )}

            {isProducer && !isCollabBeat && (
              <div className="beat-preview__verses-limit">
                <div className="beat-preview__verses-limit-head">
                  <VersesLimitHeading />
                  <span className="beat-preview__verses-limit-current">
                    Current: <strong>{verseCount}</strong> / {beat.verses_count}
                  </span>
                </div>
                <div className="beat-preview__verses-limit-control">
                  <label htmlFor="verses-count">Set limit</label>
                  <select
                    id="verses-count"
                    value={beat.verses_count}
                    onChange={e => handleVersesCountChange(parseInt(e.target.value, 10))}
                    disabled={isUpdatingVersesCount}
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                  </select>
                  {isUpdatingVersesCount && <span className="beat-preview__updating">Updating...</span>}
                </div>
                <p className="beat-preview__verses-limit-hint">
                  You can change this any time. Rappers will see the limit but cannot modify it.
                </p>
              </div>
            )}

            {authState.user && !isProducer && !isCollabBeat && (
              <div className="beat-preview__verses-limit">
                <div className="beat-preview__verses-limit-head">
                  <VersesLimitHeading />
                  <span className="beat-preview__verses-limit-badge">Set by producer</span>
                </div>
                <div className="beat-preview__verses-limit-foot">
                  <span className="beat-preview__verses-limit-current">
                    Current: <strong>{verseCount}</strong> / {beat.verses_count}
                  </span>
                  {verseLimitReached && (
                    <span className="beat-preview__limit-msg beat-preview__limit-msg--inline">Verse limit reached</span>
                  )}
                </div>
              </div>
            )}

            {(beat.tags.length > 0 || !isListener) && (
              <div className="beat-preview__hero-tags">
                {beat.tags.length > 0 ? (
                  <div className="beat-preview__tags-list">
                    {beat.tags.map(tag => (
                      <span key={tag} className="beat-preview__tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span />
                )}
                {!isListener && (
                  <span className="beat-preview__hero-votes" aria-label={`${voteCount || 0} votes`}>
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 6.364L12 20.364l-7.682-7.682a4.5 4.5 0 010-6.364z" />
                    </svg>
                    {voteCount || 0}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="beat-preview__stats">
            <div className="beat-preview__stat">
              <span className="beat-preview__stat-value">{voteCount || 0}</span>
              <span className="beat-preview__stat-label">Votes</span>
            </div>
            <div className="beat-preview__stat">
              <span className="beat-preview__stat-value">{verseCount}</span>
              <span className="beat-preview__stat-label">Verses</span>
            </div>
            <div className="beat-preview__stat">
              <span className="beat-preview__stat-value">{beat.bpm}</span>
              <span className="beat-preview__stat-label">BPM</span>
            </div>
            <div className="beat-preview__stat">
              <span className="beat-preview__stat-value">{beat.genre}</span>
              <span className="beat-preview__stat-label">Genre</span>
            </div>
          </div>
        </div>
      </section>

      <audio ref={audioRef} onEnded={() => stop()} className="beat-preview__audio-hidden" />

      <section className="beat-preview__body">
        <div className="max-w-6xl mx-auto container-padding">
          <div className="beat-preview__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'details'}
              className={`beat-preview__tab ${activeTab === 'details' ? 'beat-preview__tab--active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'verses'}
              className={`beat-preview__tab ${activeTab === 'verses' ? 'beat-preview__tab--active' : ''}`}
              onClick={() => setActiveTab('verses')}
            >
              Verses
              <span className="beat-preview__tab-count">{verseCount}</span>
            </button>
          </div>

          {deleteError && <p className="beat-preview__inline-error">{deleteError}</p>}

          {activeTab === 'details' ? (
            <div className="beat-preview__panel" role="tabpanel">
              <h2 className="beat-preview__panel-title">More info</h2>
              <dl className="beat-preview__info-grid">
                <div className="beat-preview__info-item">
                  <dt>Title</dt>
                  <dd>{beat.title}</dd>
                </div>
                <div className="beat-preview__info-item">
                  <dt>Producer</dt>
                  <dd>
                    <Link to={`/profile/${beat.producer_id}`}>{beat.producer?.username}</Link>
                  </dd>
                </div>
                <div className="beat-preview__info-item">
                  <dt>Category</dt>
                  <dd>{beat.genre}</dd>
                </div>
                <div className="beat-preview__info-item">
                  <dt>BPM</dt>
                  <dd>{beat.bpm}</dd>
                </div>
                <div className="beat-preview__info-item">
                  <dt>Location</dt>
                  <dd>{beat.producer?.state || '—'}</dd>
                </div>
                <div className="beat-preview__info-item">
                  <dt>Visibility</dt>
                  <dd>{beat.is_public ? 'Public' : 'Private'}</dd>
                </div>
                {!isListener && (
                  <div className="beat-preview__info-item">
                    <dt>Votes</dt>
                    <dd>{voteCount || 0}</dd>
                  </div>
                )}
                <div className="beat-preview__info-item beat-preview__info-item--full">
                  <dt>Uploaded</dt>
                  <dd>{new Date(beat.created_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}</dd>
                </div>
              </dl>

              {beat.tags.length > 0 && (
                <div className="beat-preview__tags">
                  <span className="beat-preview__tags-label">Tags</span>
                  <div className="beat-preview__tags-list">
                    {beat.tags.map(tag => (
                      <span key={tag} className="beat-preview__tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="beat-preview__panel beat-preview__panel--verses" role="tabpanel">
              <div className="beat-preview__verses-header">
                <h2 className="beat-preview__panel-title">
                  {isCollabBeat ? 'Takes on this beat' : 'Verses on this beat'}
                </h2>
                {canRecord && (
                  <button
                    type="button"
                    className="beat-preview__record-cta"
                    onClick={requestRecording}
                  >
                    <span className="beat-preview__record-dot" />
                    Record your verse
                  </button>
                )}
              </div>
              {isCollabBeat && (
                <p className="beat-preview__verses-privacy">
                  {hasPublishedMix
                    ? isProducer
                      ? 'A mix verse is published. Recording is closed on this beat.'
                      : 'A mix verse is published on this beat — recording is closed.'
                    : isProducer
                      ? 'Every take submitted on this beat is listed here, and only you can see them until you publish one as the mix.'
                      : 'Takes on a collab beat stay private between each artist and the producer — you will only see published ones and your own.'}
                </p>
              )}
              <VersesList
                key={versesKey}
                beatId={beatId}
                beatProducerId={beat.producer_id}
              />
            </div>
          )}
        </div>
      </section>

      <RecordingAudioCheckModal
        open={showAudioCheck}
        onClose={() => setShowAudioCheck(false)}
        onPassed={handleAudioCheckPassed}
        beatTitle={beat?.title}
      />

      {showRecorder && (
        <VerseRecorder
          beat={beat}
          requireAudioCheck
          onClose={() => setShowRecorder(false)}
          onSuccess={onVerseUploaded}
        />
      )}

      <ConfirmDeleteModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteBeat}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default BeatDetailsPage;
